import { colors } from './colors'

export interface Theme {
  colors: {
    shadow: string
    backdrop: string
    skeleton: {
      backgroundColor: string
      foregroundColor: string
    }
    background: {
      screen: string
      splashScreen: string
      textInput: string
      otpInputError: string
      buttonPrimaryEnabled: string
      buttonPrimaryDisabled: string
      buttonPrimaryGradient1: string
      buttonPrimaryGradient2: string
      buttonSecondaryEnabled: string
      secondaryGray: string
      secondaryGrayLight: string
      secondaryGrayOpacity008: string
      secondaryGrayOpacity01: string
      secondaryBlue: string
      tertiaryWhite: string
      tertiaryBlur: string
      semiTransparent: string
      cardItemRoot: string
      profitFlag: string
      loseFlag: string
      bottomSheet: string
      icon: string
      referralIconBg: string
      arcProgressBackground: string
      arcProgressFill: string
      roundedProgressBackground: string
      roundedProgressFill: string
      horizontalProgressBackground: string
      horizontalProgressFill: string
      kemCardPurchase: string
      savingTransferOut: string
      cardFlag: string
      radioButton: string
    }
    label: {
      default: string
      primary: string
      emphasis: string
      disabled: string
      clickable: string
      clickableUnderlined: string
      description: string
      warningDescription: string
      buttonPrimaryEnabled: string
      buttonPrimaryDisabled: string
      buttonSecondaryGrayLightEnabled: string
      buttonContouredGray: string
      tertiaryWhite: string
      labelTextInput: string
      textTextInput: string
      textInputError: string
      inputErrorMessage: string
      firstLetterAvatar: string
      amount: string
      flagProfit: string
      flagLosses: string
      success: string
      refunded: string
      failed: string
      pending: string
      processing: string
      semiTransparent: string
      semiTransparentKemCardTabIcons: string
      grayOpacity07: string
      secondaryGray: string
      notVerified: string
      verified: string
      verificationInProgress: string
    }
    border: {
      inputError: string
      inputTyping: string
    }
    icon: {
      primary: string
      secondary: string
      default: string
      disabled: string
      iconCard: string
      iconHeader: string
      iconTextInput: string
      roundedInputCleanButton: string
      activeInputIcon: string
      disabledInputIcon: string
      activeIconTabNav: string
      disabledIconTabNav: string
      notVerified: string
      verified: string
      verificationInProgress: string
      ellipsis_primary: string
      ellipsis_secondary: string
      ellipsis_warning_error: string
    }
  }
}

export const darkTheme: Theme = {
  colors: {
    shadow: colors.white_scales.white,
    backdrop: colors.black_scales.black_opacity_05,
    skeleton: {
      backgroundColor: colors.gray_scales.transparent_gray_01,
      foregroundColor: colors.gray_scales.transparent_gray_02,
    },
    background: {
      screen: colors.gray_scales.gray_super_dark,
      splashScreen: colors.black_scales.full_black,
      textInput: colors.blue_scales.cyan_blue_dark_2,
      otpInputError: colors.red_scales.transparent_red,
      buttonPrimaryEnabled: colors.green_scales.cyan_green_dark,
      buttonPrimaryDisabled: colors.blue_scales.cyan_blue_mid,
      buttonPrimaryGradient1: colors.purple_scales.medium_purple,
      buttonPrimaryGradient2: colors.purple_scales.bluish_purple,
      buttonSecondaryEnabled: colors.blue_scales.medium_dark_cyan_blue,
      secondaryGray: colors.blue_scales.medium_dark_cyan_blue,
      secondaryGrayLight: colors.white_scales.white,
      secondaryGrayOpacity008: colors.gray_scales.transparent_gray_008,
      secondaryGrayOpacity01: colors.gray_scales.transparent_gray_01,
      secondaryBlue: colors.blue_scales.dark_blue,
      tertiaryWhite: colors.white_scales.white,
      tertiaryBlur: colors.gray_scales.transparent_gray_04,
      semiTransparent: colors.gray_scales.transparent_gray_02,
      cardItemRoot: colors.blue_scales.cyan_blue_dark_2,
      profitFlag: colors.green_scales.transparent_green_light, // rgba(0, 207, 70, 0.10)
      loseFlag: colors.gray_scales.transparent_gray_01, // rgba(255, 255, 255, 0.10)
      bottomSheet: colors.blue_scales.cyan_blue_dark,
      icon: colors.blue_scales.medium_dark_cyan_blue,
      referralIconBg: colors.blue_scales.blue_sky,
      arcProgressBackground: colors.blue_scales.cyan_blue_dark_2,
      arcProgressFill: colors.purple_scales.dark_purple,
      roundedProgressBackground: colors.blue_scales.medium_dark_cyan_blue,
      roundedProgressFill: colors.purple_scales.dark_purple,
      horizontalProgressBackground: colors.gray_scales.transparent_gray_01,
      horizontalProgressFill: colors.green_scales.light_green_mid_2,
      kemCardPurchase: colors.purple_scales.purple,
      savingTransferOut: colors.black_scales.full_black,
      cardFlag: colors.gray_scales.transparent_gray_01,
      radioButton: colors.purple_scales.light_purple,
    },
    label: {
      default: colors.white_scales.white,
      primary: colors.purple_scales.light_purple,
      emphasis: colors.green_scales.medium_green,
      disabled: colors.blue_scales.cyan_blue_mid,
      clickable: colors.purple_scales.light_purple,
      clickableUnderlined: colors.white_scales.white,
      description: colors.gray_scales.opaque_gray,
      warningDescription: colors.blue_scales.cyan_blue_mid,
      buttonPrimaryEnabled: colors.white_scales.white,
      buttonPrimaryDisabled: colors.gray_scales.opaque_gray,
      buttonSecondaryGrayLightEnabled: colors.blue_scales.cyan_blue_dark,
      buttonContouredGray: colors.white_scales.white,
      tertiaryWhite: colors.blue_scales.cyan_blue_dark,
      labelTextInput: colors.gray_scales.opaque_gray,
      textTextInput: colors.white_scales.white,
      textInputError: colors.red_scales.pinkish_red,
      inputErrorMessage: colors.red_scales.pinkish_red,
      firstLetterAvatar: colors.white_scales.white,
      amount: colors.gray_scales.grayish_white, // For balance amounts - matches React Native
      flagProfit: colors.green_scales.cyan_green_dark_2, // #00DB4B
      flagLosses: colors.red_scales.pinkish_red, // #F32C3E
      success: colors.green_scales.medium_green,
      refunded: colors.blue_scales.blue_sky,
      failed: colors.red_scales.pinkish_red,
      pending: colors.yellow_scales.yellow_dark,
      processing: colors.blue_scales.light_blue,
      semiTransparent: colors.gray_scales.transparent_gray_03,
      semiTransparentKemCardTabIcons: colors.gray_scales.transparent_gray_04,
      grayOpacity07: colors.gray_scales.transparent_gray_07,
      secondaryGray: colors.blue_scales.medium_dark_cyan_blue,
      notVerified: colors.red_scales.additional_red,
      verified: colors.white_scales.white,
      verificationInProgress: colors.orange_scales.orange,
    },
    border: {
      inputError: colors.red_scales.pinkish_red,
      inputTyping: colors.purple_scales.light_purple,
    },
    icon: {
      primary: colors.purple_scales.light_purple,
      secondary: colors.gray_scales.light_gray,
      default: colors.white_scales.white,
      disabled: colors.blue_scales.cyan_blue_mid,
      iconCard: colors.gray_scales.opaque_gray,
      iconHeader: colors.white_scales.white,
      iconTextInput: colors.blue_scales.cyan_blue_mid,
      roundedInputCleanButton: colors.blue_scales.cyan_blue_mid,
      activeInputIcon: colors.white_scales.white,
      disabledInputIcon: colors.gray_scales.opaque_gray,
      activeIconTabNav: colors.white_scales.white,
      disabledIconTabNav: colors.blue_scales.cyan_blue_mid,
      notVerified: colors.red_scales.additional_red,
      verified: colors.blue_scales.blue_dark,
      verificationInProgress: colors.orange_scales.orange,
      ellipsis_primary: colors.blue_scales.light_medium_blue,
      ellipsis_secondary: colors.orange_scales.orange,
      ellipsis_warning_error: colors.red_scales.pinkish_red,
    },
  },
}
